import { useEffect, useState } from "react";
import { hivesApi } from "../../services/hivesApi";
import { treatmentsApi } from "../../services/treatmentsApi";
import { useToast } from "../../context/ToastContext";
import { Modal } from "../common/Modal";
import { Button } from "../common/Button";

/**
 * Dedicated "Batch Treatment" dialog referenced (but never built) by
 * BatchActionBar's old placeholder toast. Lets the user pick a catalog
 * treatment, dosage, and application date, then logs it for every
 * currently-selected hive via POST /hives/batch-treatment.
 */
export function BatchTreatmentDialog({ open, onClose, hiveIds, initialParams, onApplied }) {
  const { notify } = useToast();
  const [catalog, setCatalog] = useState([]);
  const [form, setForm] = useState({ treatment_id: "", application_date: new Date().toISOString().slice(0, 10), dosage: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    treatmentsApi.listCatalog().then((r) => setCatalog(r.treatments)).catch(() => setCatalog([]));
    setForm({
      treatment_id: initialParams?.treatment_id ? String(initialParams.treatment_id) : "",
      application_date: initialParams?.application_date || new Date().toISOString().slice(0, 10),
      dosage: initialParams?.dosage || "",
    });
  }, [open, initialParams]);

  async function submit() {
    if (!form.treatment_id) return notify("Choose a treatment", "warning");
    setSubmitting(true);
    try {
      const result = await hivesApi.batchTreatment({
        hive_ids: hiveIds,
        treatment_id: Number(form.treatment_id),
        application_date: form.application_date,
        dosage: form.dosage.trim() || undefined,
      });
      const failed = result.errors?.length || 0;
      notify(
        failed > 0
          ? `Treatment logged for ${result.logged} of ${result.requested} hive(s); ${failed} failed`
          : `Treatment logged for ${result.logged} of ${result.requested} hive(s)`,
        failed > 0 ? "warning" : "success"
      );
      onApplied?.();
      onClose();
    } catch (err) {
      notify(err.apiMessage || "Could not apply treatment", "danger");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Apply Treatment"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting}>
            {submitting ? "Applying…" : `Apply to ${hiveIds.length} hive(s)`}
          </Button>
        </>
      }
    >
      <div style={{ display: "grid", gap: "var(--space-3)" }}>
        <p style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
          This logs a treatment application for {hiveIds.length} selected hive(s).
        </p>
        <label>
          Treatment
          <select id="batch-treatment-dialog-treatment-id" name="treatment-id"
            value={form.treatment_id}
            onChange={(e) => setForm((f) => ({ ...f, treatment_id: e.target.value }))}
            className="touch-target"
            style={{ display: "block", width: "100%" }}
          >
            <option value="">Choose treatment…</option>
            {catalog.map((t) => <option key={t.treatment_id} value={t.treatment_id}>{t.name}</option>)}
          </select>
        </label>
        <label>
          Application date
          <input id="batch-treatment-dialog-application-date" name="application-date"
            type="date"
            value={form.application_date}
            onChange={(e) => setForm((f) => ({ ...f, application_date: e.target.value }))}
            className="touch-target"
            style={{ display: "block", width: "100%" }}
          />
        </label>
        <label>
          Dosage (optional)
          <input id="batch-treatment-dialog-dosage" name="dosage"
            value={form.dosage}
            onChange={(e) => setForm((f) => ({ ...f, dosage: e.target.value }))}
            className="touch-target"
            style={{ display: "block", width: "100%" }}
            placeholder="e.g. 2 strips"
            maxLength={100}
          />
        </label>
      </div>
    </Modal>
  );
}
